import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import {
  Plus,
  Search,
  FileText,
  Sparkles,
  DollarSign,
  Package,
  Calendar,
  CheckCircle2,
  ArrowRight,
  BarChart3,
  Clock,
  AlertTriangle,
  Zap,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { useAuthStore } from "../store/auth.store";
import { useShopVertical } from "../lib/useShopVertical";
import { AnalysisCard } from "../components/analysis/AnalysisCard";
import type { Analysis } from "@erc/shared";

const API = (import.meta.env.VITE_API_URL || "") + "/api";
const getToken = () => localStorage.getItem("erc_token");

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
  trend?: string;
  trendUp?: boolean;
}

function StatCard({ label, value, icon: Icon, color, trend, trendUp }: StatCardProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
          {trend && (
            <p className={`mt-1 text-xs font-medium ${trendUp ? "text-green-600" : "text-red-500"}`}>
              {trend}
            </p>
          )}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  );
}

function getUpcomingEvents() {
  const now = new Date();
  const events = [
    { name: "Valentine's Day", date: new Date(now.getFullYear(), 1, 14) },
    { name: "Mother's Day", date: new Date(now.getFullYear(), 4, 11) },
    { name: "Father's Day", date: new Date(now.getFullYear(), 5, 15) },
    { name: "Back to School", date: new Date(now.getFullYear(), 7, 1) },
    { name: "Halloween", date: new Date(now.getFullYear(), 9, 31) },
    { name: "Black Friday", date: new Date(now.getFullYear(), 10, 28) },
    { name: "Christmas", date: new Date(now.getFullYear(), 11, 25) },
    { name: "New Year", date: new Date(now.getFullYear() + 1, 0, 1) },
  ];

  return events
    .filter((e) => {
      const diff = (e.date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
      return diff > 0 && diff <= 90;
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(0, 3)
    .map((e) => ({
      ...e,
      daysUntil: Math.ceil((e.date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
    }));
}

const DAILY_TASKS = [
  { id: "new-listing", text: "Yeni bir listing ekle", priority: "high" as const, link: "/listing-builder" },
  { id: "check-orders", text: "Bekleyen siparişleri kontrol et", priority: "high" as const, link: "/etsy/orders" },
  { id: "check-reviews", text: "Yeni yorumları incele", priority: "medium" as const, link: "/etsy/reviews" },
  { id: "trend-check", text: "Trend analizini kontrol et", priority: "medium" as const, link: "/trend-intelligence" },
  { id: "optimize-seo", text: "Bir listing'in SEO skorunu iyileştir", priority: "low" as const, link: "/listing-builder" },
];

export default function DashboardPage() {
  const { user } = useAuthStore();
  const { platform, isConceptVertical } = useShopVertical();
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());

  const { data: analyses, isLoading } = useQuery({
    queryKey: ["analyses"],
    queryFn: () => apiClient.listAnalyses(),
    select: (data) => data as Analysis[],
  });

  const { data: salesStats } = useQuery({
    queryKey: ["sales-stats"],
    queryFn: async () => {
      const token = getToken();
      try {
        const res = await fetch(`${API}/sales-dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return null;
        const json = await res.json();
        return json.data;
      } catch {
        return null;
      }
    },
  });

  const { data: shops } = useQuery({
    queryKey: ["shops"],
    queryFn: () => apiClient.listShops(),
  });

  const { data: drafts } = useQuery({
    queryKey: ["builder-projects"],
    queryFn: () => apiClient.listBuilderProjects(),
  });

  const recentAnalyses = analyses?.slice(0, 6) ?? [];
  const upcomingEvents = getUpcomingEvents();

  const toggleTask = (taskId: string) => {
    setCompletedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar";


  // Taki magazasinda bu pano anlamsiz (tasarim havuzu, tekstil builder'i);
  // taki panosuna gonder. Trendyol zaten oraya yonleniyor.
  if (platform === "trendyol" || isConceptVertical) return <Navigate to="/jewelry-dashboard" replace />;

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {greeting}, {user?.name?.split(" ")[0] ?? "Kullanıcı"}
        </h2>
        <p className="mt-1 text-gray-500">
          Etsy mağazanızı yönetin, trendleri takip edin ve satış odaklı kararlar alın.
        </p>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Toplam Analiz" value={analyses?.length ?? 0} icon={Search} color="bg-indigo-500" />
        <StatCard label="Taslak Listeler" value={drafts?.length ?? 0} icon={FileText} color="bg-purple-500" />
        <StatCard label="Takip Edilen" value={shops?.length ?? 0} icon={Package} color="bg-emerald-500" />
        <StatCard
          label="Satış"
          value={salesStats?.totalOrders ?? "—"}
          icon={DollarSign}
          color="bg-amber-500"
          trend={salesStats?.revenue ? `$${salesStats.revenue}` : undefined}
          trendUp={true}
        />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Daily Tasks */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
              <Zap className="h-4 w-4 text-amber-500" />
              Günlük Görevler
            </h3>
            <span className="text-xs text-gray-500">
              {completedTasks.size}/{DAILY_TASKS.length} tamamlandı
            </span>
          </div>
          <div className="space-y-2">
            {DAILY_TASKS.map((task) => (
              <div
                key={task.id}
                className={`flex items-center gap-3 rounded-lg p-3 transition-colors ${
                  completedTasks.has(task.id) ? "bg-gray-50 opacity-60" : "bg-white hover:bg-gray-50"
                }`}
              >
                <button
                  onClick={() => toggleTask(task.id)}
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                    completedTasks.has(task.id)
                      ? "border-green-500 bg-green-500 text-white"
                      : "border-gray-300 hover:border-indigo-400"
                  }`}
                >
                  {completedTasks.has(task.id) && <CheckCircle2 className="h-3 w-3" />}
                </button>
                <span className={`flex-1 text-sm ${completedTasks.has(task.id) ? "line-through text-gray-400" : "text-gray-700"}`}>
                  {task.text}
                </span>
                <span className={`rounded px-2 py-0.5 text-xs font-medium ${
                  task.priority === "high" ? "bg-red-50 text-red-600" : task.priority === "medium" ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-500"
                }`}>
                  {task.priority === "high" ? "Yüksek" : task.priority === "medium" ? "Orta" : "Düşük"}
                </span>
                <Link to={task.link} className="text-indigo-500 hover:text-indigo-700">
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
            <Calendar className="h-4 w-4 text-indigo-500" />
            Yaklaşan Etkinlikler
          </h3>
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-gray-400">Yaklaşan özel gün yok</p>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map((event) => (
                <div key={event.name} className="flex items-center gap-3 rounded-lg border border-gray-100 p-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                    event.daysUntil <= 14 ? "bg-red-50" : event.daysUntil <= 30 ? "bg-amber-50" : "bg-blue-50"
                  }`}>
                    <Clock className={`h-5 w-5 ${
                      event.daysUntil <= 14 ? "text-red-500" : event.daysUntil <= 30 ? "text-amber-500" : "text-blue-500"
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{event.name}</p>
                    <p className={`text-xs ${event.daysUntil <= 14 ? "text-red-500 font-medium" : "text-gray-500"}`}>
                      {event.daysUntil} gün kaldı
                    </p>
                  </div>
                  {event.daysUntil <= 30 && <AlertTriangle className="h-4 w-4 text-amber-400" />}
                </div>
              ))}
            </div>
          )}
          <Link to="/trend-intelligence" className="mt-3 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700">
            Tüm takvimi gör <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Link to="/analyses/new" className="flex items-center gap-3 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50 p-4 text-indigo-700 transition-colors hover:border-indigo-400">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600">
            <Plus className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold">Yeni Analiz</p>
            <p className="text-xs text-indigo-500">Ürün analizi</p>
          </div>
        </Link>
        <Link to="/listing-builder" className="flex items-center gap-3 rounded-xl border-2 border-dashed border-purple-200 bg-purple-50 p-4 text-purple-700 transition-colors hover:border-purple-400">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-600">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold">Listing Oluştur</p>
            <p className="text-xs text-purple-500">AI destekli</p>
          </div>
        </Link>
        <Link to="/sales-dashboard" className="flex items-center gap-3 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50 p-4 text-emerald-700 transition-colors hover:border-emerald-400">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600">
            <BarChart3 className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold">Satış Raporu</p>
            <p className="text-xs text-emerald-500">Gelir analizi</p>
          </div>
        </Link>
        <Link to="/bulk-operations" className="flex items-center gap-3 rounded-xl border-2 border-dashed border-amber-200 bg-amber-50 p-4 text-amber-700 transition-colors hover:border-amber-400">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-600">
            <Package className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold">Toplu İşlem</p>
            <p className="text-xs text-amber-500">Hızlı düzenleme</p>
          </div>
        </Link>
      </div>

      {/* Recent analyses */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Son Analizler</h3>
          {analyses && analyses.length > 6 && (
            <Link to="/analyses" className="text-sm text-indigo-600 hover:text-indigo-700">Tümünü gör</Link>
          )}
        </div>
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
            ))}
          </div>
        ) : recentAnalyses.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 py-12 text-center">
            <Search className="mb-3 h-10 w-10 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">Henüz analiz yok</p>
            <p className="mt-1 text-xs text-gray-400">Chrome extension ile bir Etsy ürünü analiz et</p>
            <Link to="/analyses/new" className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
              İlk Analizi Başlat
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {recentAnalyses.map((analysis) => (
              <AnalysisCard key={analysis.id} analysis={analysis} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
